import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, Shield } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../context/LanguageContext';
import Avatar from '../components/Avatar';
import './FindAgentPage.css';

export default function FindAgentSuccessPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { leadId, city } = location.state || {};
  const [matchedAgents, setMatchedAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMatchedAgents = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('brokers')
        .select('*')
        .eq('city', city)
        .eq('is_verified', true)
        .order('joined_at', { ascending: false })
        .limit(3);

      if (!error && data) setMatchedAgents(data);
    } catch (err) {
      console.error('Failed to fetch agents:', err);
    } finally {
      setLoading(false);
    }
  }, [city]);

  useEffect(() => {
    if (!leadId) {
      navigate('/find-agent');
      return;
    }
    fetchMatchedAgents();
  }, [leadId, navigate, fetchMatchedAgents]);

  const cityLabel = city || t('findAgent.success.yourArea');

  return (
    <div className="fa-page">
      <div className="fa-container fa-container--success">
        <div className="fa-success-icon">
          <CheckCircle size={40} />
        </div>

        <h1 className="fa-headline fa-headline--success">{t('findAgent.success.headline')}</h1>
        <p className="fa-subline fa-subline--success">
          {t('findAgent.success.sublinePrefix')} {matchedAgents.length || '3'} {t('findAgent.success.sublineMid')} {cityLabel}.
          {' '}{t('findAgent.success.sublineSuffix')}
        </p>

        <div className="fa-success-steps">
          <div className="fa-success-step">
            <div className="fa-success-num">1</div>
            <div>
              <div className="fa-success-step-title">{t('findAgent.success.step1Title')}</div>
              <div className="fa-success-step-text">{t('findAgent.success.step1Text')}</div>
            </div>
          </div>
          <div className="fa-success-step">
            <div className="fa-success-num">2</div>
            <div>
              <div className="fa-success-step-title">{t('findAgent.success.step2Title')}</div>
              <div className="fa-success-step-text">{t('findAgent.success.step2Text')}</div>
            </div>
          </div>
          <div className="fa-success-step">
            <div className="fa-success-num">3</div>
            <div>
              <div className="fa-success-step-title">{t('findAgent.success.step3Title')}</div>
              <div className="fa-success-step-text">{t('findAgent.success.step3Text')}</div>
            </div>
          </div>
        </div>

        <h2 className="fa-matched-heading">{t('findAgent.success.matchedHeading')}</h2>

        {loading ? (
          <div className="fa-matched-loading">{t('findAgent.success.loading')}</div>
        ) : matchedAgents.length === 0 ? (
          <div className="fa-matched-empty">
            {t('findAgent.success.emptyPrefix')} {cityLabel}. {t('findAgent.success.emptySuffix')}
          </div>
        ) : (
          <div className="fa-matched-grid">
            {matchedAgents.map(agent => (
              <div key={agent.id} className="fa-matched-card">
                <div className="fa-matched-avatar">
                  <Avatar avatarUrl={agent.avatar_url} name={agent.name} size={56} />
                  {agent.is_verified && (
                    <span className="fa-verified-badge"><Shield size={11} /></span>
                  )}
                </div>
                <div className="fa-matched-name">{agent.name}</div>
                {agent.agency && <div className="fa-matched-agency">{agent.agency}</div>}
                {agent.rating != null && (
                  <div className="fa-matched-rating">★ {Number(agent.rating).toFixed(1)}</div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="fa-success-actions">
          <Link to="/" className="fa-btn fa-btn--primary">
            {t('findAgent.success.backHome')}
          </Link>
        </div>
      </div>
    </div>
  );
}
